import { apiClient } from './client'

// Response types for upload teachers
export interface UploadTeacherSuccess {
  row: number
  id: string
  email: string
  name: string
  lessonsAssigned: number
  lessons: Array<{
    lessonId: string
    subject: string
    action: 'created' | 'updated'
    streamCount: number
  }>
  temporaryCredentials: {
    email: string
    password: string
    pin: string
  }
}

export interface UploadTeacherFailed {
  row: number
  email: string
  name: string
  reason: string
}

export interface UploadTeachersResponse {
  success: boolean
  message: string
  data: {
    summary: {
      totalProcessed: number
      successfulCount: number
      failedCount: number
    }
    successful: UploadTeacherSuccess[]
    failed: UploadTeacherFailed[]
    failedRecordsCSV: string | null // Base64 encoded CSV
    failedRecordsCSVRaw: string | null // Raw CSV string
    institution: string
  }
}

// Progress callback type
export type UploadProgressCallback = (progress: {
  stage: 'uploading' | 'processing' | 'complete'
  percent: number
  message: string
}) => void

// Upload teachers from CSV file with progress tracking
export const uploadTeachers = async (
  file: File,
  institutionId?: string,
  onProgress?: UploadProgressCallback
): Promise<UploadTeachersResponse> => {
  const formData = new FormData()
  formData.append('file', file)

  if (institutionId) {
    formData.append('institutionId', institutionId)
  }

  // Notify upload starting
  onProgress?.({
    stage: 'uploading',
    percent: 0,
    message: 'Starting upload...'
  })

  const response = await apiClient.post('/teachers/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      if (progressEvent.total) {
        const uploadPercent = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        onProgress?.({
          stage: 'uploading',
          percent: Math.min(uploadPercent, 95), // Cap at 95% during upload
          message: uploadPercent < 100
            ? `Uploading file... ${uploadPercent}%`
            : 'Processing teachers...'
        })

        // When upload completes, switch to processing stage
        if (uploadPercent >= 100) {
          onProgress?.({
            stage: 'processing',
            percent: 95,
            message: 'Processing teachers... Please wait'
          })
        }
      }
    }
  })

  // Notify completion
  onProgress?.({
    stage: 'complete',
    percent: 100,
    message: 'Upload complete!'
  })

  return response.data
}

// Helper to download failed records CSV
export const downloadFailedRecordsCSV = (
  base64CSV: string,
  filename: string = 'failed_teachers.csv'
): void => {
  try {
    // Decode base64 to string
    const csvContent = atob(base64CSV)

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.style.display = 'none'

    document.body.appendChild(link)
    link.click()

    // Cleanup
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Failed to download CSV:', error)
    throw new Error('Failed to download failed records CSV')
  }
}

// Get CSV template for bulk upload
export const getTeacherUploadTemplate = (): string => {
  const headers = 'firstName,lastName,email,phone,countryCode,lessons'
  const sampleRow = 'John,Doe,john.doe@school.com,+254700000001,KE,"Math:Grade 1:Stream A | Math:Grade 1:Stream B | English:Grade 2:Stream A"'

  return `${headers}\n${sampleRow}`
}

// Helper to download template
export const downloadTeacherTemplate = (): void => {
  const template = getTeacherUploadTemplate()
  const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url
  link.download = 'teacher_upload_template.csv'
  link.style.display = 'none'

  document.body.appendChild(link)
  link.click()

  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
